import Reveal from "@/components/base/Reveal";
import type { StoryPost } from "@/mocks/stories";

export default function StoryArticleBody({ story }: { story: StoryPost }) {
  const half = Math.ceil(story.paragraphs.length / 2);
  const lead = story.paragraphs.slice(0, half);
  const tail = story.paragraphs.slice(half);

  return (
    <section className="sun-glaze bg-background-50 py-16 md:py-24">
      <div className="w-full px-4 md:px-8">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <p className="text-lg md:text-2xl font-heading text-foreground-800 leading-relaxed">
              {story.excerpt}
            </p>
            <div className="h-px w-16 bg-accent-500 my-10"></div>
          </Reveal>

          <div className="space-y-6">
            {lead.map((paragraph, index) => (
              <Reveal key={paragraph.slice(0, 24)} delay={index < 2 ? index * 60 : 0}>
                {index === 0 ? (
                  <p className="text-base md:text-lg text-foreground-700 leading-relaxed first-letter:font-heading first-letter:text-5xl md:first-letter:text-6xl first-letter:font-semibold first-letter:text-primary-700 first-letter:mr-3 first-letter:float-left first-letter:leading-[0.8]">
                    {paragraph}
                  </p>
                ) : (
                  <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                    {paragraph}
                  </p>
                )}
              </Reveal>
            ))}
          </div>

          <Reveal>
            <blockquote className="my-12 border-l-2 border-accent-500 pl-6 md:pl-8">
              <i className="ri-double-quotes-l text-3xl text-accent-300"></i>
              <p className="font-heading text-2xl md:text-3xl italic text-foreground-900 leading-snug mt-3">
                {story.quote}
              </p>
              <footer className="text-sm text-foreground-600 mt-4">
                — {story.author}, {story.village}
              </footer>
            </blockquote>
          </Reveal>

          <div className="space-y-6">
            {tail.map((paragraph, index) => (
              <Reveal key={`tail-${paragraph.slice(0, 24)}-${index}`} delay={index === 0 ? 60 : 0}>
                <p className="text-base md:text-lg text-foreground-700 leading-relaxed">
                  {paragraph}
                </p>
              </Reveal>
            ))}
          </div>

          <Reveal>
            <div className="mt-12 pt-6 border-t border-background-200 flex flex-wrap items-center justify-between gap-4">
              <span className="flex items-center gap-2 text-sm text-foreground-600">
                <i className="ri-map-pin-2-line text-accent-500"></i>
                Written from {story.village}
              </span>
              <span className="flex items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wider text-foreground-500">
                  Share
                </span>
                {["ri-facebook-circle-line", "ri-twitter-x-line", "ri-whatsapp-line", "ri-link"].map(
                  (icon) => (
                    <span
                      key={icon}
                      className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 border border-background-200 text-foreground-700 cursor-pointer hover:border-accent-300 hover:text-accent-600 transition-colors"
                    >
                      <i className={icon}></i>
                    </span>
                  ),
                )}
              </span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}